/**
 * Uma linha por funil habilitado: icone, nome, selos (tipo, ativo, SAUDE),
 * descricao com os numeros do periodo, as seis medidas com variacao,
 * responsavel, "Abrir funil" e o PROXIMO PASSO derivado dos dados.
 *
 * Decisoes nao obvias:
 *  1. A descricao e uma FRASE com os numeros dentro ("412 abandonaram, 87
 *     voltaram..."), nao mais tres celulas: e assim que o lojista conta a
 *     historia do funil para si mesmo. Ela sempre parte do fato ocorrido
 *     (abandonou, venceu) -- nunca "vai vencer".
 *  2. O sinal de saude e um `Badge` com ponto E texto, e a frase de motivo
 *     vem logo abaixo, na faixa do proximo passo: cor sozinha nao diz nada.
 *  3. O responsavel e estado LOCAL da previa (o backend nao tem essa coluna
 *     ainda); a troca mostra "Salvo na previa" num `role="status"` com altura
 *     reservada, para a linha nao pular quando o selo aparece e some.
 *  4. Em duas linhas (identidade + acoes em cima, medidas embaixo) em vez da
 *     linha unica da referencia: com a lateral do painel, seis medidas mais
 *     seletor e botao nao cabem em 1280px sem espremer.
 */

import { useId } from 'react';
import { Badge, Field, Icon, LinkButton, Panel, Select, type BadgeTone, type IconName } from '../../../components/ui';
import { formatBRLFromCents, formatCount, formatRatio } from '../../../lib/format';
import type { FunnelKind, TeamMember } from '../../../types';
import type { Diagnostico } from '../lib/saude';
import type { FunnelSummary } from '../types';
import { GradeMetricas } from './GradeMetricas';

interface InfoFunil {
  readonly titulo: string;
  readonly selo: string;
  readonly seloTone: BadgeTone;
  readonly icone: IconName;
  readonly iconeClasse: string;
  readonly rota: string;
  readonly semDado: string;
}

export const FUNIL_INFO: Readonly<Record<FunnelKind, InfoFunil>> = {
  carrinho_abandonado: {
    titulo: 'Funil de Carrinho Abandonado',
    selo: 'Carrinho abandonado',
    seloTone: 'accent',
    icone: 'carrinho',
    iconeClasse: 'bg-accent/15 text-accent',
    rota: '/funis/carrinho',
    semDado: 'Quem preencheu o checkout e não pagou em 30 min: quantos abandonaram, quantos voltaram e pagaram depois e quem ainda dá para recuperar.',
  },
  expirados: {
    titulo: 'Funil de Expirados',
    selo: 'Expirados',
    seloTone: 'critical',
    icone: 'calendario',
    iconeClasse: 'bg-status-critical/15 text-status-critical',
    rota: '/funis/expirados',
    semDado: 'Quem teve a assinatura vencida: quantos voltaram a pagar depois e quem ainda está fora.',
  },
};

const PAINEL_DA_SAUDE: Readonly<Record<Diagnostico['saude'], 'good' | 'warning' | 'critical' | 'elevated'>> = {
  saudavel: 'good',
  atencao: 'warning',
  critico: 'critical',
  pausado: 'critical',
  sem_dado: 'elevated',
};

export function LinhaFunil({
  kind,
  resumo,
  feeBps,
  equipe,
  responsavelId,
  onResponsavel,
  salvo,
  diagnostico,
}: {
  readonly kind: FunnelKind;
  /** `null` quando o resumo do periodo nao trouxe este funil. */
  readonly resumo: FunnelSummary | null;
  readonly feeBps: number;
  /** `null` enquanto a equipe carrega. */
  readonly equipe: readonly TeamMember[] | null;
  readonly responsavelId: string | null;
  readonly onResponsavel: (id: string | null) => void;
  /** Mostra o selo "Salvo na previa" logo depois de uma troca. */
  readonly salvo: boolean;
  readonly diagnostico: Diagnostico;
}): JSX.Element {
  const info = FUNIL_INFO[kind];
  const tituloId = useId();
  const membro = equipe?.find((m) => m.id === responsavelId) ?? null;
  const ativo = resumo?.active ?? true;
  const semEquipe = equipe === null || equipe.length === 0;

  return (
    <li>
      <article aria-labelledby={tituloId} className="flex flex-col gap-4 rounded-2xl border border-line bg-surface p-5 shadow-soft">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex min-w-0 flex-1 items-start gap-4">
            <span aria-hidden="true" className={`grid h-12 w-12 shrink-0 place-items-center rounded-xl ${info.iconeClasse}`}>
              <Icon name={info.icone} size="lg" />
            </span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 id={tituloId} className="text-base font-semibold text-ink">
                  {info.titulo}
                </h2>
                <Badge tone={info.seloTone} icon={info.icone}>
                  {info.selo}
                </Badge>
                {ativo ? (
                  <Badge tone="good" dot>
                    Ativo
                  </Badge>
                ) : (
                  <Badge tone="neutral" dot>
                    Pausado
                  </Badge>
                )}
                <Badge tone={diagnostico.tone} dot>
                  <span className="sr-only">Saúde: </span>
                  {diagnostico.rotulo}
                </Badge>
              </div>
              <p className="mt-1 max-w-2xl text-sm text-ink-muted">{descrever(kind, resumo, info)}</p>
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex items-end gap-2">
                <span aria-hidden="true" className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-ink/10 text-xs font-bold text-ink">
                  {membro === null ? <Icon name="usuarios" size="sm" /> : iniciais(membro.name)}
                </span>
                <Field label="Responsável" className="w-48">
                  {({ id }) => (
                    <Select
                      id={id}
                      size="sm"
                      value={responsavelId ?? ''}
                      disabled={semEquipe}
                      onChange={(event) => onResponsavel(event.target.value === '' ? null : event.target.value)}
                    >
                      {equipe === null ? (
                        <option value="">Carregando equipe…</option>
                      ) : equipe.length === 0 ? (
                        <option value="">Sem equipe cadastrada</option>
                      ) : (
                        <>
                          <option value="">Sem responsável</option>
                          {equipe.map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.name}
                            </option>
                          ))}
                        </>
                      )}
                    </Select>
                  )}
                </Field>
              </div>
              <LinkButton to={info.rota} iconRight="seta-dir">
                Abrir funil
              </LinkButton>
            </div>
            <p role="status" className="h-5 text-right text-xs">
              {salvo ? (
                <Badge tone="good" icon="check">
                  Salvo na prévia
                </Badge>
              ) : null}
            </p>
          </div>
        </div>

        {resumo === null ? (
          <p className="border-t border-line/70 pt-4 text-sm text-ink-subtle">Sem dados deste funil no período.</p>
        ) : (
          <GradeMetricas metricas={resumo} anterior={resumo.previous} feeBps={feeBps} compacta className="border-t border-line/70 pt-4" />
        )}

        <Panel tone={PAINEL_DA_SAUDE[diagnostico.saude]} className="flex flex-wrap items-start justify-between gap-3 px-4 py-3">
          <div className="flex min-w-0 flex-1 items-start gap-3">
            <span aria-hidden="true" className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-surface text-ink">
              <Icon name={diagnostico.saude === 'saudavel' ? 'check' : diagnostico.saude === 'sem_dado' ? 'info' : 'raio'} size="sm" />
            </span>
            <div className="min-w-0 text-sm">
              <p className="text-ink">
                <span className="font-semibold">{diagnostico.rotulo}.</span> {diagnostico.motivo}
              </p>
              <p className="mt-0.5 text-ink-muted">
                <span className="font-medium text-ink">Próximo passo:</span> {diagnostico.proximoPasso.texto}
              </p>
            </div>
          </div>
          {diagnostico.proximoPasso.rota !== null && diagnostico.proximoPasso.rotulo !== null ? (
            <LinkButton to={diagnostico.proximoPasso.rota} variant="secondary" size="sm" iconRight="seta-dir" className="shrink-0">
              {diagnostico.proximoPasso.rotulo}
            </LinkButton>
          ) : null}
        </Panel>
      </article>
    </li>
  );
}

function Numero({ valor }: { readonly valor: number }): JSX.Element {
  return <strong className="font-semibold tabular-nums text-ink">{formatCount(valor)}</strong>;
}

function descrever(kind: FunnelKind, resumo: FunnelSummary | null, info: InfoFunil): JSX.Element {
  if (resumo === null) {
    return <>{info.semDado}</>;
  }
  const contato = resumo.enteredCount === 0 ? null : (
    <>
      {' '}
      O motor chegou a <Numero valor={resumo.contactedCount} /> ({formatRatio(resumo.contactedCount, resumo.enteredCount)}).
    </>
  );
  if (kind === 'carrinho_abandonado') {
    return (
      <>
        Quem preencheu o checkout e não pagou em 30 min: <Numero valor={resumo.enteredCount} /> abandonaram, <Numero valor={resumo.recoveredCount} /> voltaram e
        pagaram depois e <Numero valor={resumo.openCount} /> ainda dão para recuperar
        {resumo.openAmountCents > 0 ? <> ({formatBRLFromCents(resumo.openAmountCents)} parados)</> : null}.{contato}
      </>
    );
  }
  return (
    <>
      Quem teve a assinatura vencida: <Numero valor={resumo.enteredCount} /> venceram no período, <Numero valor={resumo.recoveredCount} /> voltaram a pagar depois e{' '}
      <Numero valor={resumo.openCount} /> ainda estão fora{resumo.openAmountCents > 0 ? <> ({formatBRLFromCents(resumo.openAmountCents)} parados)</> : null}.{contato}
    </>
  );
}

function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter((p) => p.length > 0);
  const primeira = partes[0]?.charAt(0) ?? '';
  const ultima = partes.length > 1 ? partes[partes.length - 1]?.charAt(0) ?? '' : '';
  return `${primeira}${ultima}`.toUpperCase();
}
